import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import {
  SERVICE_SPAWN, SERVICE_ZONE_ID, createServiceCorridor, serviceCorridorAmbience, serviceCorridorFixtures, serviceCorridorSolids,
} from '@/world/serviceCorridor';

/**
 * Beat 4's service corridor as a room module. Until session 82 it was a
 * construction branch in `main.ts`.
 *
 * **The room is static.** The patrol, the dog, the mat and the door that
 * opens are beat 4's (`CorridorDirector`, still wired in `main.ts`), and so is
 * the door leaf's collider: the room leaves it out, and beat 4 builds it as
 * the one collider it can take away again. Outside beat 4 the doorway is open,
 * as it always was.
 *
 * `?dress=0` is not honoured, as the legacy branch never honoured it.
 */
const DOOR_LEAF = 'personnel-door-leaf';

function serviceEnvironment(): RoomEnvironment {
  const zone = getZone(SERVICE_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${SERVICE_ZONE_ID}"`);
  return {
    zone,
    lighting: {
      fixtures: serviceCorridorFixtures(),
      // Nearly straight down, and stronger than either hall's. There is no
      // outside here and no pretence of one; what this directional is for is
      // contact — the point lights cast no shadow, so without it the bin, the
      // sacks and the cabinet all read as printed on the floor. In a room whose
      // whole mechanic is "which of these things can I get behind", that is not
      // a mood problem, it is a legibility problem.
      keyDirection: { x: -0.12, y: 0.98, z: 0.16 },
      keyIntensity: 0.28,
      // The L's bounding box is 15.6 × 19.6. One extent has to cover both legs.
      shadowExtent: 20,
      // **Generous, and it is the opposite call from the fattening hall's.**
      // That room's low ambient is what keeps the gaps between its pools dark,
      // and D-081 is that this room has no gaps and no dark. A corridor 2.4 m
      // wide with pale painted blockwork bounces light off both walls onto
      // everything in it; a mean ambient here would invent shadow the fiction
      // says is not available to her.
      hemiIntensity: 0.22,
      ambientIntensity: 0.1,
      // Five fixtures at intensity 6 and range 7 deliver roughly 1.2 under a
      // fitting and 0.9 between two of them. Anchoring at 1.5 puts the whole
      // corridor high on the ramp and keeps the difference between "under a
      // light" and "between lights" small — which is the picture the room is
      // supposed to make. See ZoneLightingOptions.exposureReference for what
      // happens to a room with practicals that leaves this at its default.
      exposureReference: 1.5,
    },
    background: { kind: 'rig' },
    spawn: SERVICE_SPAWN,
    ambience: serviceCorridorAmbience(),
  };
}

function createServiceRoom(capabilities: RoomCapabilities): RoomModule {
  const room = createServiceCorridor();
  capabilities.scene.add(room);
  const colliders: ColliderHandle[] = serviceCorridorSolids()
    .filter((solid) => solid.name !== DOOR_LEAF)
    .map((solid) => capabilities.colliders.add(solid));
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: beat 4's director owns everything that moves */ },
    renderStep(): void { /* static */ },
    framing: () => null,
    diagnostics: () => ({ kind: 'none' }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      capabilities.scene.remove(room);
    },
  };
}

export const SERVICE_ROOM_MODULE: RoomModuleDefinition = {
  room: 'service',
  environment: serviceEnvironment,
  create: (_options, capabilities) => createServiceRoom(capabilities),
};
